import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Table, TableHead, TableBody, TableCell, TableRow, Box, TablePagination, Tooltip, Typography, Collapse, TableSortLabel, TableContainer } from '@mui/material';

// comps
import DeleteDialog, { DeleteDialogProps } from '../dialogs/deleteDialog';

// icons
import { ReactComponent as EditIcon } from '../../assets/icons/edit-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check-icon.svg';
import { ReactComponent as ClearIcon } from '../../assets/icons/clear-icon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as DocumentIcon } from '../../assets/icons/document-icon.svg';
import { ReactComponent as SortIcon } from '../../assets/icons/sort-arrow-icon.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/export-icon.svg';
import { ReactComponent as PasteIcon } from '../../assets/icons/download-icon.svg';

// constants
import { MOMENT_TIME_FORMAT } from '../../constants/date_format';
import { colors } from '../../theme';
import DialogTemplate from '../dialogs/dialogTemplate';

interface TableProps {
    customsize: boolean;
    borderradius: string;
}

interface TableRowProps {
    isEditMode?: boolean;
    clickable?: boolean;
    padding?: any;
    isExpand?: boolean;
    closed?: boolean;
    disabled?: boolean;
    activeCell?: any;
    highlight?: boolean;
}

type Order = 'asc' | 'desc';

function handleTimeComparison(time: any) {
    if (moment.isMoment(time)) {
        return time;
    }

    const timeStr = time.toString();

    if (timeStr.includes('PM')) {
        if (timeStr.startsWith('12')) {
            return moment(timeStr, MOMENT_TIME_FORMAT).format(MOMENT_TIME_FORMAT);
        } else {
            return moment(timeStr, MOMENT_TIME_FORMAT).add(12, 'hours').format(MOMENT_TIME_FORMAT);
        }
    } else {
        return moment(timeStr, MOMENT_TIME_FORMAT).format(MOMENT_TIME_FORMAT);
    }
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T, orderByObject?: string, order?: string) {
    const orderByStr = orderBy?.toString().toLowerCase();
    let compareA = a[orderBy];
    let compareB = b[orderBy];
    // Object column handling
    if (orderByObject) {
        compareA = compareA[orderByObject];
        compareB = compareB[orderByObject];
    }
    // Handle empty values
    if (orderBy) {
        // Read 0 as a proper value
        if (compareA !== 0 && compareB !== 0) {
            if (order === 'asc') {
                if (!compareB) return 1;
                if (!compareA) return -1;
            } else if (order === 'desc') {
                if (!compareB) return -1;
                if (!compareA) return 1;
            }
        }
    }
    // Handle time
    if (orderByStr?.includes('timein') || orderByStr?.includes('timeout')) {
        const compareTimeA = handleTimeComparison(compareA);
        const compareTimeB = handleTimeComparison(compareB);

        if (compareTimeB < compareTimeA) {
            return -1;
        }
        if (compareTimeB > compareTimeA) {
            return 1;
        }
        return 0;
    } else {
        if (compareB < compareA) {
            return -1;
        }
        if (compareB > compareA) {
            return 1;
        }
        return 0;
    }
}

function getComparator<Key extends keyof any>(order: Order, orderBy: Key, orderByObject?: string): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy, orderByObject, order) : (a, b) => -descendingComparator(a, b, orderBy, orderByObject, order);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export interface DataGridProps {
    data: any;
    columns: any;
    onRowClick?: any;
    isEdit?: boolean;
    renderRows: ((data: any, onChange?: any, handleInputFocus?: any) => any) | ((setExpandableRow?: any, data?: any, expandedRow?: any, setExpandedRow?: any, onChange?: any) => any);
    onSaveAsync?: (data: any) => Promise<boolean>;
    expandable?: boolean;
    renderExpandable?: (data: any, expandableRow: any, setExpandableRow: any) => any;
    activeCell?: any;
    clickable?: boolean;
    dataItemKey: string;
    padding?: any;
    isDelete?: boolean;
    onDelete?: (data: any) => Promise<boolean>;
    optOutIsDelete?: (data: any) => any;
    deleteDialogProps?: DeleteDialogProps;
    isSave?: boolean;
    customsize?: boolean;
    borderradius?: string;
    topHeight?: number;
    resetPage?: boolean;
    buttonComp?: any;
    onEdit?: (data: any) => void;
    onEditClickCallback?: (id: string) => void;
    onCancelClickCallback?: (id: string) => void;
    onButtonClick?: any;
    handleCancel?: any;
    isFileUploaded?: boolean;
    extraOnAction?: any;
    disabledKey?: string;
    height?: string;
    isClearSorting?: boolean;
    onHeaderClick?: any;
    isScrolling?: boolean;
    expandByDefault?: boolean;
    highlightItemKey?: string;
    isCopyAndPaste?: boolean;
    onCopy?: (sourceData: any) => any;
    renderPasteDialogBody?: (sourceData: any, destinationData: any, handleClose: any) => any;
    isDownloadDoc?: boolean;
    optOutIsDownloadDoc?: (data: any) => any;
    onDownloadDoc?: (data: any) => Promise<void>;
    forceHeaderTextOnSingleLine?: boolean;
    // Controlled pagination props
    page?: number;
    onPageChange?: (newPage: number) => void;
    onResetPage?: () => void;
}

const DataGrid = (props: DataGridProps) => {
    const {
        data,
        columns,
        onRowClick,
        isEdit,
        renderRows,
        onSaveAsync,
        activeCell,
        clickable,
        dataItemKey,
        padding,
        isDelete,
        onDelete,
        optOutIsDelete,
        isSave,
        customsize,
        borderradius,
        topHeight,
        resetPage,
        buttonComp,
        onEdit,
        onEditClickCallback,
        onCancelClickCallback,
        onButtonClick,
        handleCancel,
        expandable,
        renderExpandable,
        isFileUploaded,
        extraOnAction,
        disabledKey,
        height,
        isClearSorting,
        onHeaderClick,
        isScrolling,
        expandByDefault,
        highlightItemKey,
        isCopyAndPaste,
        onCopy,
        renderPasteDialogBody,
        isDownloadDoc,
        optOutIsDownloadDoc,
        onDownloadDoc,
        forceHeaderTextOnSingleLine = false,
        // Controlled pagination props
        page: controlledPage,
        onPageChange,
        onResetPage,
    } = props;

    const [internalPage, setInternalPage] = useState(0);
    const page = controlledPage !== undefined ? controlledPage : internalPage;
    const handlePageChange = onPageChange || setInternalPage;
    const [previous, setPrevious] = useState(null);
    const [rowData, setRowData] = useState([...data]);
    const [expandedRow, setExpandedRow] = useState();
    const [deleteDialog, setDeleteDialog] = useState({
        dataItem: null,
        isOpen: false,
    });
    const [selectedCell, setSelectedCell] = useState({});
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [orderByObject, setOrderByObject] = useState(null);
    const [copyModeActive, setCopyModeActive] = useState(false);
    const [openPasteDialog, setOpenPasteDialog] = useState(false);
    const [sourceData, setSourceData] = useState(null);
    const [destinationData, setDestinationData] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageHeight, setPageHeight] = useState(600); // Default height

    const navHeight = 52;

    // Calculate pageHeight and rowsPerPage after mount to avoid SSR/window issues
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const calculateDimensions = () => {
                const screenHeight = window.innerHeight;
                const calculatedPageHeight = Math.max(100, screenHeight - (navHeight + topHeight + 60));
                const calculatedRowsPerPage = Math.max(1, Math.trunc(calculatedPageHeight / 45));
                
                setPageHeight(calculatedPageHeight);
                setRowsPerPage(calculatedRowsPerPage);
            };
            
            // Calculate immediately to avoid flash
            calculateDimensions();
            
            const handleResize = () => {
                calculateDimensions();
            };
            
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [topHeight]);

    // Ensure rowsPerPage is always valid (defensive)
    const safeRowsPerPage = rowsPerPage > 0 ? rowsPerPage : 10;
    const safePageHeight = pageHeight > 0 ? pageHeight : 600;

    const handleSave = async (dataItem: any) => {
        const isSuccess = await onSaveAsync(dataItem);

        if (!isSuccess) return;

        onToggleEditMode(dataItem.id);
    };

    const handleDeleteDialog = (dataItem: any) => {
        setDeleteDialog({ ...deleteDialog, dataItem, isOpen: !deleteDialog.isOpen });
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({ ...deleteDialog, dataItem: null, isOpen: false });
    };

    const handleClosePasteDialog = () => {
        setOpenPasteDialog(false);
    };

    const handleDelete = async () => {
        const dataItem = deleteDialog.dataItem;
        const isSuccess = await onDelete(dataItem);
        if (isSuccess) handleCloseDeleteDialog();
    };

    const onToggleEditMode = (id: any) => {
        setRowData((state) => {
            return rowData.map((data: any) => {
                if (data.isEditMode) {
                    return { ...data, isEditMode: false };
                }
                if (data[dataItemKey] === id) {
                    setPrevious({ ...data });

                    return { ...data, isEditMode: !data.isEditMode };
                }
                return data;
            });
        });
    };

    const onToggleCopyMode = (id: any) => {
        setRowData((state) => {
            return rowData.map((data: any) => {
                if (data.isCopyMode) {
                    return { ...data, isCopyMode: false };
                }
                if (data[dataItemKey] === id) {
                    setPrevious({ ...data });
                    return { ...data, isCopyMode: !data.isCopyMode };
                }
                return data;
            });
        });
    };

    const onChange = (name: any, value: any, row: any, dataType: string = null) => {
        const id = row[dataItemKey];
        const newRows = rowData.map((data) => {
            let isUniqueMatch = data[dataItemKey] === id;

            if (isUniqueMatch) {
                if (dataType === 'checkbox') {
                    return { ...data, [name]: !value };
                } else if (dataType === 'fileUpload') {
                    return { ...data, examFileId: name, examFileName: value };
                } else return { ...data, [name]: value };
            }
            return data;
        });
        setRowData(newRows);
    };

    const onCancel = (data: any) => {
        const id = data[dataItemKey];
        const fileId = data.examFileId;
        // When a new row is added (id is always 0)
        if (isFileUploaded) {
            extraOnAction(fileId);
        }

        if (!previous || id === 0) {
            handleCancel();
        } else {
            const newRows = rowData.map((row) => {
                if (row[dataItemKey] === id) {
                    if (!previous[id]) {
                        return previous;
                    } else return { ...previous[id], isEditMode: !row.isEditMode };
                }
                return row;
            });
            setRowData(newRows);
            setPrevious((state) => {
                delete state[id];
                return state;
            });
        }
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * safeRowsPerPage - rowData.length) : 0;

    const handleChangePage = (event: any, newPage: any) => {
        handlePageChange(newPage);
    };

    useEffect(() => {
        setRowData(data);
        if (resetPage) {
            handlePageChange(0);
            onResetPage?.();
        }
    }, [data]);

    useEffect(() => {
        if (activeCell) setSelectedCell(activeCell);
    }, [activeCell]);

    const handleInputFocus = (data: any) => {
        setSelectedCell(data);
    };

    const handleClose = (isSucessful: boolean) => {
        if (isSucessful) {
            handleClosePasteDialog();
        } else {
            toast.error('Error while processing paste');
        }
    };

    const createSortHandler = (property: string, objectProperty?: string) => {
        // handle header sorting (manual)
        if (onHeaderClick) onHeaderClick();
        handleRequestSort(property, objectProperty);
    };

    const handleRequestSort = (property: string, objectProperty?: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        // Object column handling
        if (objectProperty) setOrderByObject(objectProperty);
        else if (!objectProperty) setOrderByObject(null);
        setOrderBy(property);
    };

    const rows = useMemo(() => {
        // if no sort selected, don’t run stableSort at all
        if (!orderBy) return rowData;

        return stableSort(rowData, getComparator(order, orderBy, orderByObject && orderByObject));
    }, [rowData, order, orderBy, orderByObject]);


    useEffect(() => {
        setOrderBy(null);
        setOrderByObject(null);
    }, [isClearSorting]);

        return (
        <TableContainer sx={{ maxHeight: safePageHeight, mt: '10px' }}>
            <StyledTable stickyHeader customsize={customsize} borderradius={borderradius}>
                <TableHead>
                    <StyledTableHeaders clickable={clickable} padding={padding} $forceSingleLine={forceHeaderTextOnSingleLine}>
                        {columns &&
                            columns.map((item: any, index: any) => {
                                return (
                                    <TableCell sx={item.sx && item.sx} key={index} align={'center'} sortDirection={orderBy === item.id ? order : false}>
                                        <Tooltip title={item.isTooltip ? item.tooltipText : ''} placement="top" arrow>
                                            <StyledTableSortLabel
                                                active={orderBy === item.id}
                                                hideSortIcon={item.disabled}
                                                direction={orderBy === item.id ? order : 'asc'}
                                                onClick={() => {
                                                    if (item.disabled) return;
                                                    if (item.objectId) {
                                                        createSortHandler(item.id, item.objectId);
                                                    } else createSortHandler(item.id);
                                                }}
                                                IconComponent={SortIcon}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '1.5em', p: '0px' }}>
                                                    {item.label ? item.label : ''}
                                                </Typography>
                                            </StyledTableSortLabel>
                                        </Tooltip>
                                    </TableCell>
                                );
                            })}
                        {isEdit && <TableCell></TableCell>}
                    </StyledTableHeaders>
                </TableHead>
                <TableBody sx={{ height: 'max-content' }}>
                    {rowData && rowData.length > 0 ? (
                        (!isScrolling ? rows.slice(page * safeRowsPerPage, page * safeRowsPerPage + safeRowsPerPage) : rows).map((data: any, i: any) => {

                            let isSelected = false;
                            let isHighlightable = false;
                            if (Object.keys(selectedCell)?.length > 0) {
                                isSelected = selectedCell[dataItemKey] === data[dataItemKey];
                                if (dataItemKey) {
                                    isSelected = isSelected && selectedCell[dataItemKey] === data[dataItemKey];
                                }
                            }
                            if (deleteDialog.dataItem) isSelected = deleteDialog.dataItem === data;
                            if (data[highlightItemKey]) isHighlightable = data[highlightItemKey];

                            return expandable ? (
                                <ExpandableRow
                                    key={`${i} + ${data[dataItemKey]}`}
                                    renderRows={renderRows}
                                    columns={columns}
                                    renderExpandable={renderExpandable}
                                    data={data}
                                    padding={padding}
                                    expandedRow={expandedRow}
                                    setExpandedRow={setExpandedRow}
                                    activeCell={activeCell}
                                    disabledKey={disabledKey}
                                    onChange={onChange}
                                    expandByDefault={expandByDefault}
                                />
                            ) : (
                                <StyledTableRow
                                    key={`${i} + ${data[dataItemKey]}`}
                                    clickable={clickable}
                                    padding={padding}
                                    selected={isSelected}
                                    onClick={() => {
                                        if (clickable) {
                                            onRowClick(data);
                                        } else return;
                                    }}
                                    highlight={isHighlightable}
                                >
                                    {isEdit ? renderRows(data, onChange, handleInputFocus) : renderRows(data)}
                                    {isEdit && (
                                        <CommandTableCell sx={{ width: isDelete ? 73 : isSave ? '3%' : '5%', textAlign: 'center' }}>
                                            {data.isEditMode ? (
                                                <IconContainer>
                                                    <CheckIcon 
                                                        id="check" 
                                                        onClick={() => handleSave(data)}
                                                        disabled={isDownloading}
                                                    />
                                                    <ClearIcon 
                                                        id="clear" 
                                                        onClick={() => {
                                                            if (onCancelClickCallback && data?.id) {
                                                                onCancelClickCallback(data?.id)
                                                            }
                                                            onCancel(data);
                                                        }}
                                                        disabled={isDownloading}
                                                    />
                                                </IconContainer>
                                            ) : !data.isEditMode && isSave ? (
                                                <CheckIcon 
                                                    id="check" 
                                                    onClick={() => handleSave(data)}
                                                    disabled={isDownloading}
                                                />
                                            ) : (
                                                <IconContainer>
                                                    {buttonComp && (
                                                        <span style={{ height: '20px' }} onClick={() => onButtonClick(data)}>
                                                            {buttonComp}
                                                        </span>
                                                    )}
                                                    {!copyModeActive && (
                                                        <EditIcon
                                                            id="edit"
                                                            onClick={() => {
                                                                const isInEdit = rowData.find((dataItem) => dataItem.isEditMode);
                                                                if (!copyModeActive) {
                                                                    if (!isInEdit) {
                                                                        if (onEditClickCallback && data?.id) {
                                                                            onEditClickCallback(data?.id);
                                                                        }
                                                                        if (onEdit) onEdit(data);
                                                                        else onToggleEditMode(data[dataItemKey]);
                                                                    } else toast.warning('Only one item can be edited.');
                                                                } else {
                                                                    toast.warning('Cannot edit when copy mode is active');
                                                                }
                                                            }}
                                                            disabled={isDownloading}
                                                        />
                                                    )}
                                                    {isDelete && !copyModeActive && (!optOutIsDelete || !optOutIsDelete(data)) && (
                                                        <DeleteIcon 
                                                            id="clear" 
                                                            onClick={() => handleDeleteDialog(data)}
                                                            disabled={isDownloading}
                                                        />
                                                    )}
                                                    {isDownloadDoc && !copyModeActive && (!optOutIsDownloadDoc || !optOutIsDownloadDoc(data)) && (
                                                        <Tooltip
                                                            title={'Generate document'}
                                                            arrow
                                                            placement="top"
                                                            PopperProps={{
                                                                modifiers: [
                                                                    {
                                                                        name: 'offset',
                                                                        options: {
                                                                            offset: [0, -5],
                                                                        },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            <DocumentIcon
                                                                id="download"
                                                                onClick={async () => {
                                                                    setIsDownloading(true);
                                                                    try {
                                                                        await onDownloadDoc(data)
                                                                    } finally {
                                                                        setIsDownloading(false);
                                                                    }
                                                                }}
                                                                disabled={isDownloading}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {isCopyAndPaste && (
                                                        <>
                                                            {!copyModeActive && (
                                                                <Tooltip
                                                                    title={'Copy'}
                                                                    arrow
                                                                    placement="top"
                                                                    PopperProps={{
                                                                        modifiers: [
                                                                            {
                                                                                name: 'offset',
                                                                                options: {
                                                                                    offset: [0, -5],
                                                                                },
                                                                            },
                                                                        ],
                                                                    }}
                                                                >
                                                                    <CopyIcon
                                                                        id="copy"
                                                                        onClick={() => {
                                                                            setCopyModeActive(true);
                                                                            setSourceData(data);
                                                                            setDestinationData(null);
                                                                            if (onCopy) {
                                                                                onCopy(data);
                                                                            }
                                                                        }}
                                                                        disabled={isDownloading}
                                                                    />
                                                                </Tooltip>
                                                            )}

                                                            {copyModeActive && sourceData?.id !== data.id && (
                                                                <Tooltip
                                                                    title={'Paste'}
                                                                    arrow
                                                                    placement="top"
                                                                    PopperProps={{
                                                                        modifiers: [
                                                                            {
                                                                                name: 'offset',
                                                                                options: {
                                                                                    offset: [0, -5],
                                                                                },
                                                                            },
                                                                        ],
                                                                    }}
                                                                >
                                                                    <PasteIcon
                                                                        id="paste"
                                                                        onClick={() => {
                                                                            setCopyModeActive(false);
                                                                            setOpenPasteDialog(true);
                                                                            setDestinationData(data);
                                                                        }}
                                                                        disabled={isDownloading}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                            {copyModeActive && sourceData?.id === data.id && (
                                                                <Tooltip
                                                                    title={'Cancel Copy'}
                                                                    arrow
                                                                    placement="top"
                                                                    PopperProps={{
                                                                        modifiers: [
                                                                            {
                                                                                name: 'offset',
                                                                                options: {
                                                                                    offset: [0, -5],
                                                                                },
                                                                            },
                                                                        ],
                                                                    }}
                                                                >
                                                                    <ClearIcon
                                                                        id="clear"
                                                                        onClick={() => {
                                                                            setCopyModeActive(false);
                                                                            setSourceData(null);
                                                                            setDestinationData(null);
                                                                        }}
                                                                        disabled={isDownloading}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    )}
                                                </IconContainer>
                                            )}
                                        </CommandTableCell>
                                    )}
                                </StyledTableRow>
                            );
                        })
                    ) : (
                        <TableCell colSpan={columns?.length + 1} sx={{ textAlign: 'center', borderBottom: `1px solid ${colors.grey.light}`, height: height }}>
                            No data found.
                        </TableCell>
                    )}
                    {!isScrolling && (
                        <StyledTableFooter>
                            <TablePagination count={rowData?.length} onPageChange={handleChangePage} rowsPerPage={safeRowsPerPage} rowsPerPageOptions={[-1]} page={page} />
                        </StyledTableFooter>
                    )}
                </TableBody>
            </StyledTable>
            {deleteDialog.isOpen && <DeleteDialog {...props.deleteDialogProps} onClose={handleCloseDeleteDialog} onDelete={handleDelete} />}
            {openPasteDialog && (
                <DialogTemplate maxWidth="md" title={'Confirm Paste'} renderContent={() => renderPasteDialogBody(sourceData, destinationData, handleClose)} onClose={handleClosePasteDialog} />
            )}
        </TableContainer>
    );
};

const ExpandableRow = (props) => {
    const { renderRows, data, columns, renderExpandable, padding, expandedRow, setExpandedRow, activeCell, disabledKey, onChange, expandByDefault } = props;
    const [expandableRow, setExpandableRow] = useState([]);

    const hasDatePassed = moment().isAfter(moment(data[disabledKey]), 'day');

    return (
        <>
            <StyledTableRow padding={padding} selected={activeCell === data || activeCell?.id === data?.id} disabled={hasDatePassed}>
                {renderRows(setExpandableRow, data, expandedRow, setExpandedRow, onChange)}
            </StyledTableRow>

            <StyledTableRow
                selected={expandByDefault && (activeCell === data || activeCell?.id === data?.id)}
                style={{ width: '100%' }}
                padding={'5px 10px'}
                isExpand={true}
                closed={!expandByDefault && expandedRow !== data.id}
            >
                <TableCell colSpan={columns.length + 1}>
                    <Collapse in={expandedRow === data.id || expandByDefault} timeout="auto" unmountOnExit>
                        {renderExpandable(data, expandableRow, setExpandableRow)}
                    </Collapse>
                </TableCell>
            </StyledTableRow>
        </>
    );
};

export default DataGrid;

const StyledTable = styled(Table)<TableProps>`
    && {
        background-color: ${colors.white};
        border-radius: ${(props) => (props.customsize ? props.borderradius : '5px')};
        box-shadow: ${(props) => !props.customsize && `0px 2px 10px rgba(0, 0, 0, 0.05)`};
        /* margin-top: 10px; */
        padding: 2px 12px;
        overflow-y: hidden;
    }
`;

const StyledTableHeaders = styled(TableRow)<TableRowProps & { $forceSingleLine?: boolean }>`
    th {
        padding: 5px;
        border-bottom: 1px solid ${colors.grey.light};

        ${(props) =>
            props.$forceSingleLine && {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
            }}
    }
`;

const StyledTableRow = styled(TableRow)<TableRowProps>`
    && {
        background-color: ${(props) => (props.isEditMode && colors.bg) || (props.highlight && colors.blue.light)};

        &:hover {
            background-color: ${(props) => !props.isEditMode && !props.isExpand && colors.blue.light};
            cursor: ${(props) => (props.isEditMode || props.clickable ? 'pointer' : 'default')};
        }

        td {
            padding: ${(props) => (props.padding ? props.padding : props.isEditMode ? '5px 2px' : '10px')};
            border-bottom: 1px solid ${colors.grey.light};
        }

        ${(props) =>
            props.closed && {
                td: {
                    padding: 0,
                    borderBottom: 'none',
                },
            }}

        ${(props) =>
            props.disabled && {
                backgroundColor: '#F5F5F5',
                // textDecoration: 'line-through',
                '&:hover': {
                    backgroundColor: '#F5F5F5',
                    cursor: 'default',
                },
            }}
    }
`;

const StyledTableFooter = styled(TableRow)`
    && {
        .MuiTablePagination-toolbar {
            min-height: 20px;
        }

        td {
            border-bottom: none;
        }
    }
`;

const CommandTableCell = styled(TableCell)`
    && {
        svg {
            width: 20px;
            height: auto;

            path {
                fill: ${colors.grey.medium};
            }
        }

        &:hover {
            cursor: pointer;
        }

        #edit {
            width: 22px;
            margin-left: 5px;
            
            &:hover {
                path {
                    fill: ${colors.blue.main};
                }
            }
        }

        #check {
            width: 22px;
            margin-left: 5px;

            &:hover {
                path {
                    fill: ${colors.green.main};
                }
            }
        }

        #clear {
            width: 22px;
            margin-left: 5px;

            &:hover {
                path {
                    fill: ${colors.red.main};
                }
            }
        }

        #download {
            width: 22px;
            margin-left: 5px;

            &:hover {
                path {
                    fill: ${colors.green.main};
                }
            }
        }

        #copy {
            width: 22px;
            margin-left: 5px;

            &:hover {
                path {
                    fill: ${colors.blue.main};
                }
            }
        }

        #paste {
            width: 22px;
            margin-left: 5px;

            &:hover {
                path {
                    fill: ${colors.blue.main};
                }
            }
        }
    }
`;

const IconContainer = styled.span`
    display: flex;
    align-items: center;
`;

const StyledTableSortLabel = styled(TableSortLabel)`
    && {
        .MuiTableSortLabel-icon {
            margin-left: 0 !important;
        }
        &:hover {
            color: ${(props) => props.hideSortIcon && colors.black};
            cursor: ${(props) => (props.hideSortIcon ? 'default' : 'pointer')};
        }
    }
`;
